import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '@env';
import { AuthenticatedUser, LoginResponse } from '../models/auth.model';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private currentUser = new BehaviorSubject<AuthenticatedUser | null>(null);
  currentUser$ = this.currentUser.asObservable();

  private readonly tokenKey = 'insure_auth_token';
  private readonly userKey = 'insure_auth_user';

  constructor(
    private httpClient: HttpClient,
    private cookieService: CookieService,
  ) {
    this.restoreSession();
  }

  login(username: string, password: string): Observable<LoginResponse> {
    const url = `${environment.ApiBaseUrl}/api/auth/login`;
    return this.httpClient
      .post<LoginResponse>(url, { username, password })
      .pipe(
        tap((resp: LoginResponse) => {
          if (resp?.accessToken && resp?.user) {
            this.setToken(resp.accessToken);
            this.storeUser(resp.user);
            this.currentUser.next(resp.user);
          }
        }),
      );
  }

  logout(): void {
    this.clearAuthData();
  }

  getCurrentUser(): AuthenticatedUser | null {
    return this.currentUser.value;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  getToken(): string | null {
    return this.cookieService.get(this.tokenKey) || null;
  }

  getUserRoles(): string[] {
    return this.currentUser.value?.roles || [];
  }

  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  }

  clearAuthData(): void {
    this.cookieService.delete(this.tokenKey, '/');
    sessionStorage.removeItem(this.userKey);
    this.currentUser.next(null);
  }

  private setToken(token: string): void {
    this.cookieService.set(this.tokenKey, token, {
      path: '/',
      secure: true,
      sameSite: 'Strict',
    });
  }

  private restoreSession(): void {
    const token = this.getToken();
    if (token && this.isAuthenticated()) {
      const storedUser = this.getStoredUser();
      if (storedUser) {
        this.currentUser.next(storedUser);
        return;
      }
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const roles = payload.role
          ? typeof payload.role === 'string'
            ? payload.role.split(',')
            : [].concat(payload.role)
          : [];
        const user: AuthenticatedUser = {
          systemId: payload.systemid || '',
          name: payload.name || payload.unique_name || '',
          userName: payload.unique_name || '',
          roles,
          isResetPassword: payload.isresetpassword === 'True',
        };
        this.storeUser(user);
        this.currentUser.next(user);
      } catch {
        this.clearAuthData();
      }
    }
  }

  private storeUser(user: AuthenticatedUser): void {
    sessionStorage.setItem(this.userKey, JSON.stringify(user));
  }

  private getStoredUser(): AuthenticatedUser | null {
    try {
      const data = sessionStorage.getItem(this.userKey);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }
}
